import { useCallback, useEffect, useState } from "react";
import useSWR from "swr";
import download from "downloadjs";
import { Modal } from "@mui/material";
import { useSelector } from "react-redux";

import PDFPreview from "./PDFPreview";
import BeefCutsForm from "./BeefCutsForm";
import { generateBeefMerchandisingPDF } from "../../utils/poster/beefPdfGenerator";
import { languages } from "../../utils/translate";

function defaultPosterTitle(retailer) {
  return (
    retailer?.retailer_name ||
    "BEEF MERCHANDISING"
  );
}

const BeefMerchandisingCard = ({ language = languages[0] }) => {
  const [fileUrl, setFileUrl] = useState();
  const [pdfBytes, setPdfBytes] = useState();
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedBeefCuts, setSelectedBeefCuts] = useState([]);
  const [posterTitle, setPosterTitle] = useState("");
  const [posterHeaderImage, setPosterHeaderImage] = useState(null);
  const [posterDefaultsReady, setPosterDefaultsReady] = useState(false);
  const retailer = useSelector((state) => state.retailer);

  const { data: beefCuts, isLoading } = useSWR({
    url: `beef_product`,
    fields: [
      "*",
      "id",
      "translations.*",
      "food_service_configurations.*",
      "primal_configurations.*",
    ],
    limit: -1,
    filter: {
      status: {
        _eq: "published",
      },
    },
  });

  useEffect(() => {
    if (!retailer?.id || posterDefaultsReady) return;
    setPosterTitle(defaultPosterTitle(retailer));
    setPosterHeaderImage(retailer.logo ?? null);
    setPosterDefaultsReady(true);
  }, [retailer, posterDefaultsReady]);

  const posterOptionsForPdf = useCallback(() => {
    if (!posterDefaultsReady) {
      return { posterTitle: defaultPosterTitle(retailer) };
    }
    return {
      headerCenterLogoAssetId: posterHeaderImage,
      posterTitle: posterTitle.trim() || defaultPosterTitle(retailer),
    };
  }, [
    posterDefaultsReady,
    posterHeaderImage,
    posterTitle,
    retailer,
  ]);

  const createPDF = async () => {
    setLoading(true);
    try {
      const storedSelectedBeefCuts = localStorage.getItem("selectedBeefCuts");
      const { fileURL, pdfBytes } = await generateBeefMerchandisingPDF(
        selectedBeefCuts?.length > 0
          ? selectedBeefCuts
          : JSON.parse(storedSelectedBeefCuts) || [],
        beefCuts,
        retailer,
        language.code,
        posterOptionsForPdf(),
      );
      setFileUrl(fileURL);
      setPdfBytes(pdfBytes);
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    localStorage.setItem("selectedBeefCuts", JSON.stringify(selectedBeefCuts));
    setModalOpen(false);
    createPDF();
  };

  const handleCutsPdfActions = (action) => {
    if (action === "view") {
      window.open(fileUrl);
    } else if (action === "download") {
      const base =
        posterTitle
          .trim()
          .replace(/[^\w\s-]/g, "")
          .replace(/\s+/g, "_")
          .slice(0, 80) || "beef_merchandising";
      const pdfBlob = new Blob([pdfBytes], { type: "application/pdf" });
      download(pdfBlob, base, "application/pdf");
    }
  };

  useEffect(() => {
    const storedSelectedBeefCuts = localStorage.getItem("selectedBeefCuts");
    if (storedSelectedBeefCuts) {
      setSelectedBeefCuts(JSON.parse(storedSelectedBeefCuts));
    }

    if (beefCuts && beefCuts.length > 0) {
      createPDF();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beefCuts, language.code]);

  return (
    <>
      <PDFPreview
        title={posterTitle.trim() || defaultPosterTitle(retailer)}
        fileUrl={fileUrl}
        loading={loading}
        showEdit={true}
        onEdit={() => setModalOpen(true)}
        onView={() => handleCutsPdfActions("view")}
        onDownload={() => handleCutsPdfActions("download")}
      />

      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <BeefCutsForm
          beefCuts={beefCuts}
          selectedBeefCuts={selectedBeefCuts}
          setSelectedBeefCuts={setSelectedBeefCuts}
          handleSubmit={handleSubmit}
          onClose={() => setModalOpen(false)}
          isLoading={isLoading}
          language={language}
          posterTitle={posterTitle}
          onPosterTitleChange={setPosterTitle}
          posterHeaderImage={posterHeaderImage}
          onPosterHeaderImageChange={setPosterHeaderImage}
        />
      </Modal>
    </>
  );
};

export default BeefMerchandisingCard;
